'use client';

import React, { createContext, useContext } from 'react';
import type { SkinZeroShellData } from '@/components/ui/skin-zero/shell-types';

export interface TenantNavCounts {
  series?: number;
  oneOfOnes?: number;
  installations?: number;
  collaborations?: number;
  exhibitions?: number;
  awards?: number;
  press?: number;
  writings?: number;
  storeProducts?: number;
}

export interface TenantThemeConfig {
  colorMode?: 'light' | 'dark' | 'auto';
  fontPair?: 'swiss-sans' | 'editorial-serif' | 'archival-mono';
  accentColor?: string;
  heroStyle?: 'statement' | 'featured-work' | 'index-grid';
}

export interface TenantIdentity {
  tenantSlug: string;
  isOrkhan: boolean;
  designId: string;
  artistName: string;
  shortName: string;
  siteDomain: string;
  contactEmail: string;
  tagline: string;
  navCounts?: TenantNavCounts;
  themeConfig?: TenantThemeConfig;
  /** Header, menu and footer data for the default template; only loaded for sites wearing it. */
  skinZeroShell?: SkinZeroShellData;
}

/**
 * orkhan.art's identity, as the root layout builds it when the CMS has nothing to add. For tests
 * and stories that render Orkhan's site; the live site always gets its identity from the layout.
 */
export const ORKHAN_TENANT_IDENTITY: TenantIdentity = {
  tenantSlug: 'orkhan-art',
  isOrkhan: true,
  designId: 'oma-original',
  artistName: 'Orkhan Mammadov',
  shortName: 'ORKHAN',
  siteDomain: 'orkhan.art',
  contactEmail: 'hey@orkhan.art',
  tagline: 'New media artist working on computational creativity',
};

/**
 * No site: a host no client owns, or a component rendered outside the root layout. Nothing in it
 * names anyone, so a component that misses its provider shows nobody's identity, Orkhan's included.
 */
export const NO_SITE_IDENTITY: TenantIdentity = {
  tenantSlug: '',
  isOrkhan: false,
  designId: 'skin-zero',
  artistName: '',
  shortName: '',
  siteDomain: '',
  contactEmail: '',
  tagline: '',
};

const TenantContext = createContext<TenantIdentity>(NO_SITE_IDENTITY);

export function TenantProvider({
  value,
  children,
}: {
  value: TenantIdentity;
  children: React.ReactNode;
}) {
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantIdentity {
  return useContext(TenantContext);
}
